import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
} from "@mhome/ui";
import LoopIcon from "@mui/icons-material/Loop";
import { formatWithPrecision, formatNumber } from "../common/number-helper";
import { convert, normalizeByUnit } from "../common/number-unit-helper";

const SLIDER_RADIUS = 16;

const NumberUnitInput = ({ valueSpec, onChange, onError, zIndex }) => {
  // Static mapping for unit display
  const unitDisplayMapping = {
    F: "°F",
    C: "°C",
    dC: "Δ°C",
    dF: "Δ°F",
  };

  // Helper function to get display unit
  const getDisplayUnit = (unit) => {
    return unitDisplayMapping[unit] || unit;
  };

  const [spec, setSpec] = useState(null);
  const [sValue, setSValue] = useState(0);
  const [tValue, setTValue] = useState(0);
  const [localValue, setLocalValue] = useState(0);
  const [unit, setUnit] = useState("");
  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);
  const [step, setStep] = useState(null);
  const [outOfRange, setOutOfRange] = useState(false);
  const isReadOnly = valueSpec.readOnly;
  const [useSlider, setUseSlider] = useState(true);
  const hasSlider = min !== null && max !== null;
  const [hideButtons, setHideButtons] = useState(false);
  const containerRef = useRef(null);
  const sliderContainerRef = useRef(null);
  const dragging = useRef(false);
  const touchStartRef = useRef(null);
  const isScrolling = useRef(false);
  const valueToCommit = useRef(null);

  // Use localValue during dragging for immediate visual feedback
  const displayValue = dragging.current ? localValue : sValue;
  const percent =
    min !== null && max !== null
      ? Math.max(0, Math.min(1, (displayValue - min) / (max - min)))
      : 0;

  // Extract precision from valueSpec with default value of 1
  const precision = valueSpec?.precision || 1;

  // Use provided zIndex or fall back to default
  const selectZIndex =
    zIndex ||
    parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--z-index-automation-input"
      ) || "1300",
      10
    );

  useEffect(() => {
    const normalized = normalizeByUnit(
      valueSpec.value,
      valueSpec.unit,
      valueSpec.dataType,
      valueSpec.min,
      valueSpec.max,
      precision
    );

    // Update valueSpec with normalized values
    valueSpec.number = normalized.number;
    valueSpec.unit = normalized.unit;
    valueSpec.min = normalized.min;
    valueSpec.max = normalized.max;

    setSpec(valueSpec);
    const value = formatNumber(Number(valueSpec.number), precision);
    setSValue(value);
    setTValue(value);
    setLocalValue(value);
    valueToCommit.current = value;
    setUnit(valueSpec.unit);
    if (valueSpec.min !== undefined) {
      setMin(valueSpec.min);
    }
    if (valueSpec.max !== undefined) {
      setMax(valueSpec.max);
    }
    const valid = verifyValue(value, valueSpec.min, valueSpec.max);
    if (!valid) {
      if (onError) {
        onError();
      }
    }
    if (valueSpec.steps) {
      for (let i = 0; i < valueSpec.steps.length; i++) {
        if (valueSpec.units[i] === valueSpec.unit) {
          if (valueSpec.steps[i] !== 0) {
            setStep(valueSpec.steps[i]);
          } else {
            setStep(1);
          }
          break;
        }
      }
    } else {
      setStep(1);
    }
    return () => {};
  }, [valueSpec]);

  // Sync localValue with sValue when not dragging
  useEffect(() => {
    if (!dragging.current) {
      setLocalValue(sValue);
    }
  }, [sValue]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        // Hide buttons if width is less than 200px (adjust threshold as needed)
        setHideButtons(width < 200);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const verifyValue = (value, minBound, maxBound) => {
    const isOutOfRange =
      (minBound !== null && value < minBound) ||
      (maxBound !== null && value > maxBound);
    setOutOfRange(isOutOfRange);
    return !isOutOfRange; // Return valid instead of isOutOfRange
  };

  const handleMove = (clientX) => {
    if (!sliderContainerRef.current) return;
    const rect = sliderContainerRef.current.getBoundingClientRect();

    let x = clientX - rect.left;
    x = Math.max(0, Math.min(rect.width, x));
    if (rect.width <= 0) return;

    const newPercent = x / rect.width;
    let newValue = newPercent * (max - min) + min;

    // Snap to step
    if (step) {
      newValue = Math.round(newValue / step) * step;
    }

    // Clamp to min/max
    newValue = Math.max(min, Math.min(max, newValue));

    // Format the value
    newValue = formatNumber(newValue, precision);

    setLocalValue(newValue); // Update local state immediately for visual feedback
    setSValue(newValue);
    setTValue(newValue);
    valueToCommit.current = newValue;
    verifyValue(newValue, min, max);
  };

  const commitChange = (value) => {
    const valid = verifyValue(value, min, max);
    if (!valid) {
      if (onError) {
        onError();
      }
    } else {
      onChange(value.toString(), unit, max, min);
    }
  };

  const handleMouseDown = (e) => {
    if (isReadOnly) return;
    e.preventDefault();
    dragging.current = true;
    handleMove(e.clientX);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!dragging.current) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    commitChange(valueToCommit.current);
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e) => {
    if (isReadOnly) return;
    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const handleTouchMove = (e) => {
    if (isScrolling.current || !touchStartRef.current) return;

    if (dragging.current) {
      e.preventDefault();
      handleMove(e.touches[0].clientX);
      return;
    }

    const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    const moveThreshold = 5;

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      if (deltaY > deltaX) {
        isScrolling.current = true;
        touchStartRef.current = null;
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      } else {
        e.preventDefault();
        dragging.current = true;
        handleMove(e.touches[0].clientX);
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isScrolling.current) {
      isScrolling.current = false;
      touchStartRef.current = null;
      return;
    }

    if (dragging.current) {
      commitChange(valueToCommit.current);
    } else {
      // Tap event
      handleMove(e.changedTouches[0].clientX);
      commitChange(valueToCommit.current);
    }

    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = null;
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  const handleUnitChange = (event) => {
    if (isReadOnly) return;
    const prev = unit;
    const cur = event.target.value;
    if (prev === cur) {
      return;
    }
    setUnit(cur);
    // val
    const newVal = convert(sValue, prev, cur, spec.dataType, true, precision);
    setSValue(newVal);
    setTValue(newVal);
    // min, max
    const newMin = convert(min, prev, cur, spec.dataType, false, precision);
    setMin(newMin);
    const newMax = convert(max, prev, cur, spec.dataType, false, precision);
    setMax(newMax);
    const valid = verifyValue(newVal, newMin, newMax);
    // step
    let foundStep = false;
    for (let i = 0; i < spec.steps.length; i++) {
      if (spec.units[i] === cur) {
        if (spec.steps[i] !== 0) {
          setStep(spec.steps[i]);
        } else {
          setStep(1);
        }
        foundStep = true;
        break;
      }
    }
    if (!foundStep) {
      setStep(1);
    }
    if (!valid) {
      if (onError) {
        onError();
      }
    } else {
      onChange(newVal, cur, newMax, newMin);
    }
  };

  const handleNumberTextChange = (event) => {
    if (isReadOnly) return;
    if (event.target.value === "") {
      setSValue(0);
      setTValue("");
    } else {
      const newValue = Number(event.target.value);
      setSValue(newValue);
      setTValue(event.target.value);
    }
    const newValue = Number(event.target.value);
    verifyValue(newValue, min, max);
  };

  const processInputValue = (value) => {
    if (value === "") {
      setSValue(0);
      setTValue(0);
      const valid = verifyValue(0, min, max);
      if (!valid) {
        if (onError) {
          onError();
        }
      } else {
        onChange("0", unit, max, min);
      }
    } else {
      const newValue = formatNumber(Number(value), precision);
      setSValue(newValue);
      setTValue(newValue);
      const valid = verifyValue(newValue, min, max);
      if (!valid) {
        if (onError) {
          onError();
        }
      } else {
        onChange(newValue.toString(), unit, max, min);
      }
    }
  };

  const handleNumberTextBlur = (event) => {
    if (isReadOnly) return;
    processInputValue(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (isReadOnly) return;
    if (event.key === "Enter") {
      event.preventDefault();
      processInputValue(event.target.value);
      event.target.blur(); // Remove focus from the input
    }
  };

  const handleIncrement = () => {
    if (isReadOnly) return;
    const newValue = formatNumber(sValue + step, precision);
    setSValue(newValue);
    setTValue(newValue);
    const valid = verifyValue(newValue, min, max);
    if (!valid) {
      if (onError) {
        onError();
      }
    } else {
      onChange(newValue.toString(), unit, max, min);
    }
  };

  const handleDecrement = () => {
    if (isReadOnly) return;
    const newValue = formatNumber(sValue - step, precision);
    setSValue(newValue);
    setTValue(newValue);
    const valid = verifyValue(newValue, min, max);
    if (!valid) {
      if (onError) {
        onError();
      }
    } else {
      onChange(newValue.toString(), unit, max, min);
    }
  };

  const handleToggle = () => {
    setUseSlider(!useSlider);
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full text-center flex flex-col items-center justify-center"
    >
      {hasSlider && useSlider && (
        <div className="flex items-center justify-center gap-2 w-full min-h-[28px]">
          <div
            ref={sliderContainerRef}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            className="relative flex-1 h-[30px] rounded-2xl overflow-visible select-none"
            style={{
              background: "hsl(var(--muted))",
              cursor: isReadOnly ? "default" : "pointer",
              opacity: isReadOnly ? 0.8 : 1,
            }}
          >
            {/* Colored track */}
            <div
              className="absolute left-0 top-0 h-full rounded-2xl pointer-events-none"
              style={{
                width: `${percent * 100}%`,
                background: "hsl(var(--primary))",
                transition: dragging.current ? "none" : "width 0.2s",
                zIndex: 1,
              }}
            />
            {/* Value display on top of slider */}
            <Typography
              className="text-sm font-medium absolute left-3 top-1/2 -translate-y-1/2 z-[2] pointer-events-none"
              style={{
                color: outOfRange ? "hsl(var(--destructive))" : "#fff",
              }}
            >
              {formatWithPrecision(tValue, precision)}
              {getDisplayUnit(unit)}
            </Typography>
          </div>
          <Button
            onClick={handleToggle}
            variant="outlined"
            disabled={isReadOnly}
            className="min-w-9 w-9 h-7 min-h-7 p-0 text-base font-semibold"
            style={{
              opacity: isReadOnly ? 0.7 : 1,
              cursor: isReadOnly ? "default" : "pointer",
            }}
          >
            <LoopIcon />
          </Button>
        </div>
      )}
      {(!hasSlider || !useSlider) && (
        <div className="flex items-center justify-center gap-2 w-full min-h-[28px]">
          {!hideButtons && (
            <Button
              onClick={handleDecrement}
              variant="outlined"
              disabled={isReadOnly}
              className="min-w-[28px] w-[28px] h-[28px] min-h-[28px] p-0 text-base font-semibold"
              style={{
                opacity: isReadOnly ? 0.7 : 1,
                cursor: isReadOnly ? "default" : "pointer",
              }}
            >
              -
            </Button>
          )}
          <TextField
            type="number"
            value={formatWithPrecision(tValue, precision)}
            onChange={handleNumberTextChange}
            onBlur={handleNumberTextBlur}
            onKeyDown={handleKeyDown}
            className="min-h-[28px] text-center"
            style={{
              minHeight: "28px",
            }}
            InputProps={{
              style: {
                minHeight: "28px",
                padding: "0 8px",
                textAlign: "center",
              },
              readOnly: isReadOnly,
            }}
            error={outOfRange}
          />
          {!hideButtons && (
            <Button
              onClick={handleIncrement}
              variant="outlined"
              disabled={isReadOnly}
              className="min-w-[28px] w-[28px] h-[28px] min-h-[28px] p-0 text-base font-semibold"
              style={{
                opacity: isReadOnly ? 0.7 : 1,
                cursor: isReadOnly ? "default" : "pointer",
              }}
            >
              +
            </Button>
          )}
          <FormControl
            className="min-w-auto w-auto relative"
            style={{
              zIndex: selectZIndex,
            }}
          >
            <Select
              id="unit-select"
              value={unit}
              onChange={handleUnitChange}
              size="small"
              className="min-h-[28px] text-center text-base"
              style={{
                zIndex: selectZIndex,
                minHeight: "28px",
              }}
            >
              {valueSpec.units.map((option) => (
                <MenuItem key={option} value={option}>
                  {getDisplayUnit(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasSlider && (
            <Button
              onClick={handleToggle}
              variant="outlined"
              disabled={isReadOnly}
              className="min-w-9 w-9 h-7 min-h-7 p-0 text-base font-semibold"
              style={{
                opacity: isReadOnly ? 0.7 : 1,
                cursor: isReadOnly ? "default" : "pointer",
              }}
            >
              <LoopIcon />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default NumberUnitInput;
